import { useEventBus } from '@vueuse/core'
import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  Timestamp,
  updateDoc,
  where,
  writeBatch,
} from 'firebase/firestore'
import { defineStore } from 'pinia'
import { useSubscriptions } from '~/composables/useSubscriptions.js'
import type { Notification, NotificationFirestoreData } from '~/shared/types/notification.js'
import { normalizeNotification } from '~/shared/utils/notifications.js'

/**
 * Store for managing notifications
 *
 * Follows the pattern of other stores in the application with:
 * - State management for notifications
 * - CRUD operations for Firestore
 * - Real-time subscription handling
 * - Helper methods for notification management
 */
export const useNotificationsStore = defineStore('notifications', {
  state: () => ({
    // Store notifications by document ID for O(1) lookups
    notifications: {} as Record<string, Notification>,
    loading: false,
    error: null as string | null,
    activeSubscriptions: [] as (() => void)[],
    unreadCount: 0,
    initialized: false,
    // Track if we're in the initial load phase
    initialLoadComplete: false,
  }),

  getters: {
    /**
     * Convert map to array for components that expect arrays
     * Sorted by creation date (newest first)
     */
    notificationsArray(): Notification[] {
      return Object.values(this.notifications).sort((a, b) => {
        // Safely handle date comparison
        const aTime = a.createdAt instanceof Date ? a.createdAt.getTime() : 0
        const bTime = b.createdAt instanceof Date ? b.createdAt.getTime() : 0
        return bTime - aTime
      })
    },

    /**
     * Get unread notifications
     */
    unreadNotifications(): Notification[] {
      return this.notificationsArray.filter(n => !n.read)
    },

    /**
     * Get notifications by category
     */
    getNotificationsByCategory: state => (category: string): Notification[] => {
      return Object.values(state.notifications)
        .filter(n => n.category === category)
        .sort((a, b) => {
          // Safely handle date comparison
          const aTime = a.createdAt instanceof Date ? a.createdAt.getTime() : 0
          const bTime = b.createdAt instanceof Date ? b.createdAt.getTime() : 0
          return bTime - aTime
        })
    },

    /**
     * Get notification by ID
     */
    getNotificationById: state => (id: string): Notification | undefined => {
      return state.notifications[id]
    },
  },

  actions: {
    /**
     * Convert Firestore document data to a Notification object
     */
    convertToNotification(docId: string, data: NotificationFirestoreData): Notification {
      return normalizeNotification({
        id: docId,
        title: data.title,
        body: data.body,
        createdAt: data.createdAt instanceof Timestamp
          ? data.createdAt.toDate()
          : new Date(data.createdAt as Date),
        read: data.read || false,
        readAt: data.readAt instanceof Timestamp
          ? data.readAt.toDate()
          : data.readAt as Date | null,
        linkType: data.linkType,
        linkTarget: data.linkTarget,
        emailSent: data.emailSent || false,
        emailScheduled: data.emailScheduled || false,
        emailSentAt: data.emailSentAt instanceof Timestamp
          ? data.emailSentAt.toDate()
          : data.emailSentAt as Date | undefined,
        category: data.category,
        priority: data.priority || 'normal',
        metadata: data.metadata || {},
      })
    },

    /**
     * Convert a Notification object to Firestore-compatible data
     */
    convertToFirestoreData(notification: Notification): NotificationFirestoreData {
      const firestoreData: NotificationFirestoreData = {
        title: notification.title,
        body: notification.body,
        createdAt: notification.createdAt,
        read: notification.read,
        readAt: notification.readAt,
        linkType: notification.linkType,
        linkTarget: notification.linkTarget,
        emailSent: notification.emailSent,
        emailScheduled: notification.emailScheduled,
        emailSentAt: notification.emailSentAt,
        category: notification.category,
        priority: notification.priority,
        metadata: notification.metadata || {},
      }

      // Clean object recursively to remove undefined values (including in nested objects)
      return this.cleanObjectForFirestore(firestoreData)
    },

    // Helper method to recursively clean objects for Firestore
    cleanObjectForFirestore(obj: NotificationFirestoreData): NotificationFirestoreData {
      const result = { ...obj }

      Object.keys(result).forEach((key) => {
        if (result[key] === undefined) {
          delete result[key]
        }
        else if (typeof result[key] === 'object' && result[key] !== null) {
          // Skip Date objects - we want to preserve them as-is
          if (!(result[key] instanceof Date)) {
            // Recursively clean nested objects (only if they're not Date objects)
            result[key] = this.cleanObjectForFirestore(result[key])
          }
        }
      })

      return result
    },

    /**
     * Create a new notification for a member
     * @param memberId ID of the member to create notification for
     * @param notification Notification data to create
     * @returns ID of the created notification
     */
    async createNotification(memberId: string, notification: Partial<Notification>): Promise<string> {
      try {
        this.loading = true

        const db = useFirestore()
        const notificationsRef = collection(db, `members/${memberId}/notifications`)
        const newNotificationRef = doc(notificationsRef)

        const newNotification: Notification = normalizeNotification({
          id: newNotificationRef.id,
          title: notification.title || '',
          body: notification.body || '',
          createdAt: new Date(),
          read: false,
          linkType: notification.linkType || 'none',
          linkTarget: notification.linkTarget,
          emailSent: false,
          emailScheduled: notification.emailScheduled || false,
          emailSentAt: undefined,
          category: notification.category || 'system',
          priority: notification.priority || 'normal',
          metadata: notification.metadata || {},
        })

        const notificationData = this.convertToFirestoreData(newNotification)
        // Use server timestamp for createdAt
        notificationData.createdAt = serverTimestamp()

        console.warn(`[Notifications] Creating new notification with ID ${newNotification.id} for member ${memberId}`)
        await setDoc(newNotificationRef, notificationData)

        // Add to local state if we are subscribed to this member's notifications
        const currentUser = useCurrentUser().value
        if (this.initialized && currentUser && memberId === currentUser.uid) {
          console.warn(`[Notifications] Updating local state for new notification ${newNotification.id}`)
          this.$patch((state) => {
            state.notifications[newNotification.id] = newNotification
            state.unreadCount += 1
          })
        }

        // If emailScheduled is true, queue an email using the email service
        if (newNotification.emailScheduled) {
          try {
            // Get member data to get the email address
            const membersStore = useMembersStore()
            const member = await membersStore.fetchMember(memberId)

            if (member && member.email) {
              console.warn(`[Notifications] Queueing email for notification ${newNotification.id} to ${member.email}`)

              // Use the email service to queue the email securely
              const emailStore = useEmailStore()
              const emailId = await emailStore.queueNotificationEmail(
                newNotification,
                member.email,
                member.name || 'Member',
                memberId,
              )

              // Update notification with email sent status if email was queued successfully
              if (emailId) {
                // Update local state if needed
                if (this.initialized && currentUser && memberId === currentUser.uid) {
                  this.$patch((state) => {
                    const notification = state.notifications[newNotification.id]
                    if (notification) {
                      notification.emailSent = true
                      notification.emailSentAt = new Date()
                    }
                  })
                }

                console.warn(`[Notifications] Email queued successfully with ID ${emailId}`)
              }
            }
            else {
              console.warn(`[Notifications] Cannot send email for notification ${newNotification.id}: Member ${memberId} has no email address`)
            }
          }
          catch (emailError) {
            console.error(`[Notifications] Error sending email for notification ${newNotification.id}:`, emailError)
            // We don't throw the error here - we want to return the notification ID even if email sending fails
          }
        }

        return newNotification.id
      }
      catch (error) {
        console.error('Error creating notification:', error)
        this.error = error instanceof Error ? error.message : 'Unknown error'
        throw error
      }
      finally {
        this.loading = false
      }
    },

    /**
     * Mark a notification as read
     * @param notificationId ID of the notification to mark as read
     * @param memberId ID of the member the notification belongs to
     */
    async markAsRead(notificationId: string, memberId: string): Promise<boolean> {
      try {
        this.loading = true

        const db = useFirestore()
        const notificationRef = doc(db, `members/${memberId}/notifications/${notificationId}`)

        await updateDoc(notificationRef, {
          read: true,
          readAt: serverTimestamp(),
        })

        // Update local state
        if (this.notifications[notificationId] && !this.notifications[notificationId].read) {
          this.$patch((state) => {
            if (state.notifications[notificationId]) {
              state.notifications[notificationId].read = true
              state.notifications[notificationId].readAt = new Date()
              state.unreadCount = Math.max(0, state.unreadCount - 1)
            }
          })
        }

        return true
      }
      catch (error) {
        console.error('Error marking notification as read:', error)
        this.error = error instanceof Error ? error.message : 'Unknown error'
        return false
      }
      finally {
        this.loading = false
      }
    },

    /**
     * Mark all notifications as read for a member
     * @param memberId ID of the member
     */
    async markAllAsRead(memberId: string): Promise<boolean> {
      try {
        this.loading = true

        const db = useFirestore()

        const notificationsRef = collection(db, `members/${memberId}/notifications`)

        // Get all unread notifications
        const q = query(notificationsRef, where('read', '==', false))
        const querySnapshot = await getDocs(q)

        if (querySnapshot.empty) {
          return true // No unread notifications to update
        }
        const batch = writeBatch(db)
        // Update each notification in a batch
        querySnapshot.forEach((docSnapshot) => {
          const notificationRef = doc(db, `members/${memberId}/notifications/${docSnapshot.id}`)
          batch.update(notificationRef, {
            read: true,
            readAt: serverTimestamp(),
          })
        })

        await batch.commit()

        // Update local state
        this.$patch((state) => {
          let unreadCountDelta = 0

          Object.keys(state.notifications).forEach((id) => {
            if (state.notifications[id] && !state.notifications[id].read) {
              state.notifications[id].read = true
              state.notifications[id].readAt = new Date()
              unreadCountDelta++
            }
          })

          state.unreadCount = Math.max(0, state.unreadCount - unreadCountDelta)
        })

        return true
      }
      catch (error) {
        console.error('Error marking all notifications as read:', error)
        this.error = error instanceof Error ? error.message : 'Unknown error'
        return false
      }
      finally {
        this.loading = false
      }
    },

    /**
     * Delete a notification
     * @param notificationId ID of the notification to delete
     * @param memberId ID of the member the notification belongs to
     */
    async deleteNotification(notificationId: string, memberId: string): Promise<boolean> {
      try {
        this.loading = true

        const db = useFirestore()
        const notificationRef = doc(db, `members/${memberId}/notifications/${notificationId}`)

        // Check if it exists and is unread before deleting
        const wasUnread = this.notifications[notificationId]
          ? !this.notifications[notificationId].read
          : false

        await deleteDoc(notificationRef)

        // Update local state
        this.$patch((state) => {
          if (wasUnread) {
            state.unreadCount = Math.max(0, state.unreadCount - 1)
          }
          delete state.notifications[notificationId]
        })

        return true
      }
      catch (error) {
        console.error('Error deleting notification:', error)
        this.error = error instanceof Error ? error.message : 'Unknown error'
        return false
      }
      finally {
        this.loading = false
      }
    },

    /**
     * Fetch notifications for a member
     * @param memberId ID of the member to fetch notifications for
     * @param limit Maximum number of notifications to fetch (optional)
     */
    async fetchNotifications(memberId: string, notificationLimit = 100): Promise<Notification[]> {
      try {
        this.loading = true

        const db = useFirestore()
        const notificationsRef = collection(db, `members/${memberId}/notifications`)

        // Query for notifications, ordered by creation date (newest first)
        const q = query(
          notificationsRef,
          orderBy('createdAt', 'desc'),
          limit(notificationLimit),
        )

        const querySnapshot = await getDocs(q)
        const notifications: Record<string, Notification> = {}
        let unreadCount = 0

        querySnapshot.forEach((doc) => {
          const notification = this.convertToNotification(
            doc.id,
            doc.data() as NotificationFirestoreData,
          )
          notifications[notification.id] = notification

          if (!notification.read) {
            unreadCount++
          }
        })

        // Fix: Use the state mutator function pattern instead of direct object assignment
        this.$patch((state) => {
          // Replace the notifications object
          state.notifications = notifications
          state.unreadCount = unreadCount
          state.initialized = true
          state.loading = false
        })

        return Object.values(notifications)
      }
      catch (error) {
        console.error('Error fetching notifications:', error)
        this.error = error instanceof Error ? error.message : 'Unknown error'
        throw error
      }
      finally {
        this.loading = false
      }
    },

    /**
     * Subscribe to notifications for a member
     * @param memberId ID of the member to subscribe to
     * @returns Function to unsubscribe
     */
    subscribeToNotifications(memberId: string): () => void {
      console.warn(`[Notifications] Setting up subscription for member: ${memberId}`)

      const db = useFirestore()
      const notificationsRef = collection(db, `members/${memberId}/notifications`)
      const { registerSubscription, hasSubscriptionWithKey, clearSubscriptionByKey } = useSubscriptions()
      const eventBus = useEventBus('new-notification')

      // Create a unique key for this subscription
      const subKey = `notifications-${memberId}`

      // Check if we already have this subscription
      if (hasSubscriptionWithKey(subKey)) {
        console.warn(`[Notifications] Subscription to notifications for member ${memberId} already exists, clearing existing subscription`)
        // Clear existing subscription to avoid duplicates
        clearSubscriptionByKey(subKey)
      }

      // Query for notifications, ordered by creation date (newest first)
      const q = query(
        notificationsRef,
        orderBy('createdAt', 'desc'),
        limit(100), // Limit to last 100 notifications
      )

      console.warn(`[Notifications] Creating onSnapshot subscription for ${memberId}, path: members/${memberId}/notifications`)

      // Subscribe to real-time updates with extra logging
      const unsubscribe = onSnapshot(
        q,
        (snapshot) => {
          const snapMeta = (snapshot as { metadata?: { hasPendingWrites?: boolean, fromCache?: boolean } }).metadata ?? {}
          console.warn(`[Notifications] Snapshot received for ${memberId} with ${snapshot.docChanges().length} changes, hasPendingWrites: ${snapMeta.hasPendingWrites ?? false}, fromCache: ${snapMeta.fromCache ?? false}`)

          // Process all changes in a single batch update for better performance
          const toAdd: Record<string, Notification> = {}
          const toRemove: string[] = []
          let unreadDelta = 0
          const newNotifications: Notification[] = []

          snapshot.docChanges().forEach((change) => {
            const notificationData = change.doc.data() as NotificationFirestoreData
            const notification = this.convertToNotification(change.doc.id, notificationData)

            if (change.type === 'added' || change.type === 'modified') {
              toAdd[notification.id] = notification

              // Calculate unread delta
              if (change.type === 'added' && !notification.read) {
                unreadDelta += 1

                // If this is a new notification (not part of initial load)
                // add it to the list of notifications to emit events for
                if (this.initialLoadComplete && change.type === 'added') {
                  newNotifications.push(notification)
                }
              }
              else if (change.type === 'modified') {
                const oldNotification = this.notifications[notification.id]
                if (oldNotification && oldNotification.read !== notification.read) {
                  unreadDelta += notification.read ? -1 : 1
                }
              }
            }
            else if (change.type === 'removed') {
              toRemove.push(notification.id)

              // Update unread count if it was an unread notification
              if (this.notifications[notification.id] && !this.notifications[notification.id]?.read) {
                unreadDelta -= 1
              }

              console.warn(`[Notifications] Removed notification: ${notification.id}`)
            }
          })

          // Apply all changes in a single patch operation
          this.$patch((state) => {
            // Add new and modified notifications
            Object.entries(toAdd).forEach(([id, notification]) => {
              state.notifications[id] = notification
            })

            // Remove deleted notifications
            toRemove.forEach((id) => {
              delete state.notifications[id]
            })

            // Update unread count
            state.unreadCount = Math.max(0, state.unreadCount + unreadDelta)

            // Set initialized flag
            state.initialized = true
          })

          // Emit events for new notifications (but only after initial load)
          if (this.initialLoadComplete && newNotifications.length > 0) {
            // Emit events for each new notification
            newNotifications.forEach((notification) => {
              eventBus.emit(notification)
              console.warn(`[Notifications] Emitted event for new notification: ${notification.title}`)
            })
          }

          console.warn(`[Notifications] After update: ${Object.keys(this.notifications).length} total notifications, ${this.unreadCount} unread`)
        },
        (error) => {
          console.error('[Notifications] Error in subscription:', error)
          this.error = error.message
        },
      )

      // Immediately load existing notifications to ensure we have data
      this.fetchNotifications(memberId)
        .then((notifications) => {
          console.warn(`[Notifications] Initial fetch completed: ${notifications.length} notifications loaded`)
          // Mark initial load as complete AFTER the first fetch completes
          this.$patch((state) => {
            state.initialLoadComplete = true
          })
        })
        .catch((error) => {
          console.error('[Notifications] Error in initial fetch:', error)
          // Still mark as completed even on error
          this.$patch((state) => {
            state.initialLoadComplete = true
          })
        })

      console.warn(`[Notifications] Registering subscription with key: ${subKey}`)

      // Store subscription for cleanup (twice)
      // 1. Locally in the store for store-specific cleanup
      this.activeSubscriptions.push(unsubscribe)

      // 2. In the global subscription manager
      // This is the important part - we're creating a wrapper function that will first call
      // the real unsubscribe and then remove it from our local activeSubscriptions array
      const wrappedUnsubscribe = () => {
        console.warn(`[Notifications] Unsubscribing from notifications for ${memberId}`)

        // Call the original unsubscribe function
        unsubscribe()

        // Remove from our local tracking array
        const index = this.activeSubscriptions.indexOf(unsubscribe)
        if (index !== -1) {
          this.activeSubscriptions.splice(index, 1)
        }
      }

      // Register the wrapped unsubscribe function
      registerSubscription(wrappedUnsubscribe, 'notifications', subKey)

      return wrappedUnsubscribe
    },

    /**
     * Clean up all active subscriptions
     */
    cleanupSubscriptions() {
      // Call all unsubscribe functions
      this.activeSubscriptions.forEach(unsubscribe => unsubscribe())
      this.activeSubscriptions = []

      // Reset store state
      this.$patch({
        notifications: {},
        loading: false,
        error: null,
        unreadCount: 0,
        initialized: false,
        initialLoadComplete: false,
      })
    },

    /**
     * Reset the store state completely
     */
    $reset() {
      // Clean up subscriptions first
      this.cleanupSubscriptions()

      // Then reset state
      this.$patch({
        notifications: {},
        loading: false,
        error: null,
        activeSubscriptions: [],
        unreadCount: 0,
        initialized: false,
        initialLoadComplete: false,
      })
    },
  },
})
